import { CommonModule } from "@angular/common";
import { ChangeDetectorRef, Component, HostListener, OnInit, ViewChild } from "@angular/core";
import {
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from "@angular/forms";
import { PrimeNGSharedModule } from "@shared/prime/prime-ng.module";
import { CalendarModule } from "primeng/calendar";
import {
  ApiService,
  Country,
  Region,
  UserRole,
} from "@core/services/api/api.service";
import {
  AuditPermissionService,
  PermissionReference,
  Users,
} from "@core/services/audit-permission.service";
import { MessageService, ScrollerOptions } from "primeng/api";
import { LoaderService } from "@core/services/gigya/loader/loader.service";
import { finalize, pairwise, pipe, skip } from "rxjs";
import { ToastMessageComponent } from "@shared/components/toast-message/toast-message.component";
import { TooltipModule } from "primeng/tooltip";
import { SuccessComponent } from "@shared/components/success/success.component";

interface AvailablePermission {
  id: number;
  name: string;
  description: string;
  isDisabled?: boolean;
  isSelected?: boolean;
}

@Component({
  selector: "app-permission-groups",
  imports: [
    CommonModule,
    ReactiveFormsModule,
    PrimeNGSharedModule,
    CalendarModule,
    ReactiveFormsModule,
    FormsModule,
    ToastMessageComponent,
    TooltipModule,
    SuccessComponent,
  ],
  templateUrl: "./permission-groups.component.html",
  styleUrl: "./permission-groups.component.scss",
})
export class PermissionGroupsComponent implements OnInit {
  formSubmitted: boolean = false;
  userForm!: FormGroup;
  showCustomerDetailsSuccessDialog: boolean = false;
  customerDetailsSuccessDialogTitle: string = "";
  customerDetailsSuccessMessage: string = "";
  users: Users[] = [];
  previousValue: any;
  regions: Region[] = [];
  country: Country[] = [];
  UserRoles: UserRole[] = [];
  permissionReference: PermissionReference[] = [];

  filteredResponsibleCountries: Country[] = [];
  date: Date | undefined;
  DataByUserID: any = {};
  availablePermissions: AvailablePermission[] = [];
  selectedPermission: AvailablePermission[] = [];
  dropdownOpen = false;
  page: number = 1;
  pageSize: number = 10;
  search: string = "";
  loading: boolean = false;
  metaData: any = {};

  onLazyLoad(event: any) {
    this.onLazyLoadUsers(event);
  }

  options: ScrollerOptions = {
    delay: 250,
    showLoader: true,
    lazy: true,
    onLazyLoad: this.onLazyLoad.bind(this),
  };

  constructor(
    private fb: FormBuilder,
    private apiService: ApiService,
    private AuditPermissionService: AuditPermissionService,
    private messageService: MessageService,
    private loaderService: LoaderService,
       private cdr: ChangeDetectorRef,
  ) {
    this.initializeForm();
  }
  ngOnInit(): void {
    this.getPermissionRole();
    this.fetchData();
    this.getPermissionReference();
  }

  initializeForm(): void {
    this.userForm = this.fb.group({
      user: ["", Validators.required],
      roleType: [{ value: null, disabled: true }, Validators.required],
      responsibleRegion: [{ value: null, disabled: true }, Validators.required],
      userCountry: [{ value: null, disabled: true }, Validators.required],
      responsibleCountry: [{ value: [], disabled: true }, Validators.required],
      issuedDate: [null, Validators.required],
      dueDate: [null, Validators.required],
    });
  }

  getPermissionRole() {
    this.AuditPermissionService.getPermissionRole().subscribe({
      next: (data) => {},
      error: (error) => {},
    });
  }

  fetchUserOptions(page: number, pageSize: number, search: string): void {
    this.loading = true;
    this.AuditPermissionService.getGlobalUserName(
      page,
      pageSize,
      search
    ).subscribe({
      next: (response) => {
        this.loading = false;
        if (response && response.users) {
          if (page === 1) {
            this.users = response.users;
          } else {
            this.users = [...this.users, ...response.users];
          }
          this.metaData = response.meta;
          this.cdr.detectChanges();
        }
      },
      error: (err) => {
        this.loading = false;
      },
    });
  }
  fetchUserPermissionById(id: any) {
    this.AuditPermissionService.getPermissionUserByID(id).subscribe({
      next: (response) => {
        this.DataByUserID = response;
        this.patchFormValues(response);

        this.updatePermissionsSelection(response.permissions);
      },
      error: (error) => {},
    });
  }

  onUserChange(event: any) {
    this.previousValue = [];
    this.userForm.get("permission")?.setValidators([Validators.required]);
    this.userForm.get("permission")?.updateValueAndValidity();
    this.fetchUserPermissionById(event.value.id);
  }
  patchFormValues(userData: any) {
    if (!userData) return;
    this.userForm.get("roleType")?.disable();
    this.userForm.get("responsibleRegion")?.disable();
    this.userForm.get("userCountry")?.disable();
    const formValues = {
      user: this.userForm.get("user")?.value,
      roleType: this.getRoleName(userData.role_id) || null,
      responsibleRegion: this.getRegionName(userData.assigned_region) || null,
      userCountry: this.getCountryNames(userData.assigned_country_ids) || null,
      issuedDate: userData.override_start_date
        ? new Date(userData.override_start_date)
        : null,
      dueDate: userData.override_end_date
        ? new Date(userData.override_end_date)
        : null,
    };
    this.userForm.patchValue(formValues);
    this.userForm.get("issuedDate")?.enable();
    this.userForm.get("dueDate")?.enable();
  }

  updatePermissionsSelection(userPermissions: any[]) {
    this.availablePermissions = this.permissionReference.map((permission) => ({
      ...permission,
      isDisabled: this.DataByUserID.role_based_permissions.includes(
        permission.id
      ),
      isSelected: this.DataByUserID.role_based_permissions.includes(
        permission.id
      ),
    }));
    this.availablePermissions = this.availablePermissions.map(
      (perm: AvailablePermission) => ({
        ...perm,
        isSelected: this.DataByUserID.overridden_permissions.includes(perm.id)
          ? true
          : perm.isSelected,
      })
    );
    this.selectedPermission = this.availablePermissions.filter((perm: any) => {
      if (perm.isSelected && !perm.isDisabled) {
        return perm;
      }
    });
    this.previousValue = [...this.DataByUserID.overridden_permissions];
  }

  getCountryNames(countryIds: number[]): string {
    if (!countryIds || countryIds.length === 0) {
      return "N/A";
    }
    const countryNames = countryIds
      .map((id) => {
        const country = this.country.find((c) => c.id === id);
        return country ? country.name : "";
      })
      .filter((name) => name !== "");
    if (countryNames.length === 0) {
      return "N/A";
    }
    return countryNames.join(", ");
  }
  getRoleName(roleId: number): string {
    const role = this.UserRoles.find((r) => r.id === roleId);
    return role ? role.name : "N/A";
  }

  getRegionName(regionId: number): string {
    const region = this.regions.find((r) => r.id === regionId);
    return region ? region.name : "N/A";
  }

  fetchData(): void {
    this.apiService.getRegions().subscribe({
      next: (response) => {
        this.regions = response.regions;
      },
      error: (err) => {},
    });
    this.apiService.getUserRoles().subscribe({
      next: (response) => {
        this.UserRoles = response.user_roles;
      },
      error: (err) => {},
    });
    this.apiService.getCountries().subscribe({
      next: (response) => {
        if (response && response.countries) {
          this.country = response.countries;
        }
      },
      error: (err) => {},
    });
  }
  formatDate(d: Date): string {
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  }

  getPermissionReference() {
    this.AuditPermissionService.getPermissionReference().subscribe({
      next: (response) => {
        this.permissionReference = response?.permissions;
      },
      error: (error) => {},
    });
  }

  showErrorToast(summary: string, detail: string) {
    this.messageService.add({
      severity: "error",
      summary: summary,
      detail: detail,
      life: 5000,
    });
  }
  showSuccessToast(summary: string, detail: string) {
    this.messageService.add({
      severity: "success",
      summary: summary,
      detail: detail,
      life: 3000,
    });
  }

  save() {
    this.formSubmitted = true;
    this.loaderService.show();

    if (this.userForm.valid) {
      const formData = this.userForm.getRawValue();
      const user_id = formData.user.id;
      const payload = {
        start_date: this.formatDate(formData.issuedDate),
        end_date: this.formatDate(formData.dueDate),
        permissions: this.selectedPermission.map((c) => c.id),
      };

      this.AuditPermissionService.postUserPermission(user_id, payload)
        .pipe(
          finalize(() => {
            this.loaderService.hide();
          })
        )
        .subscribe({
          next: (data) => {
            this.showCustomerDetailsSuccessDialog = true;
            this.customerDetailsSuccessMessage =
              "Permissions saved successfully";
            this.showToast(
              "success",
              "Success",
              "Permissions saved successfully"
            );
            this.userForm.reset();
            this.selectedPermission = [];
          },
          error: (error) => {
            this.showToast("error", "Error", error);
          },
        });
    }
  }

  @ViewChild(ToastMessageComponent) toast!: ToastMessageComponent;
  private toastMessages: { type: string; title: string; message: string }[] =
    [];

  ngAfterViewInit(): void {
    // Process any queued toast messages
    setTimeout(() => {
      this.toastMessages.forEach((msg) => {
        switch (msg.type) {
          case "success":
            this.toast.showSuccess(msg.title, msg.message);
            break;
          case "error":
            this.toast.showError(msg.title, msg.message);
            break;
          case "info":
            this.toast.showInfo(msg.title, msg.message);
            break;
          case "warn":
            this.toast.showWarn(msg.title, msg.message);
            break;
        }
      });
      this.toastMessages = [];
    });
  }
  showToast(type: string, title: string, message: string): void {
    if (!this.toast) {
      this.toastMessages.push({ type, title, message });
      return;
    }

    switch (type) {
      case "success":
        this.toast.showSuccess(title, message);
        break;
      case "error":
        this.toast.showError(title, message);
        break;
      case "info":
        this.toast.showInfo(title, message);
        break;
      case "warn":
        this.toast.showWarn(title, message);
        break;
    }
  }

  onCustomerDetailsSuccessDialogClose() {
    this.showCustomerDetailsSuccessDialog = false;
  }

  toggleDropdown() {
    this.dropdownOpen = !this.dropdownOpen;
  }
  toggleSelection(permission: any) {
    permission.isSelected = !permission.isSelected;

    if (permission.isSelected) {
      this.selectedPermission.push(permission);
    } else {
      this.selectedPermission = this.selectedPermission.filter(
        (p) => p.name !== permission.name
      );
    }
  }

  getSliceValue() {
    return this.selectedPermission
      .slice(2)
      .map((c) => c.name)
      .join(", ");
  }

  removeSelection(permission: any) {
    permission.isSelected = false;
    this.selectedPermission = this.selectedPermission.filter(
      (p) => p.name !== permission.name
    );
  }

  isPermissionChanged(): boolean {
    const previousValue = this.previousValue ?? [];
    const currentValue = this.selectedPermission.map((c) => c.id);
    const isSame =
      previousValue.length === currentValue.length &&
      previousValue.every((id: any) => currentValue.includes(id));

    return !isSame; // true → changed, false → not changed
  }

  @HostListener("document:click", ["$event"])
  clickOutside(event: MouseEvent) {
    const target = event.target as HTMLElement;
    if (!target.closest(".dropdown-container")) {
      this.dropdownOpen = false;
    }
  }

  onLazyLoadUsers(event: any) {
    if (Object.keys(this.metaData).length === 0) {
      this.fetchUserOptions(this.page, this.pageSize, this.search);
    }
    const nextPage = this.metaData?.page + 1;

    if (nextPage <= this.metaData.total_pages) {
      this.page = nextPage;
      this.fetchUserOptions(this.page, this.pageSize, this.search);
    }
  }
  onFilterUsers(event: any) {
    this.search = event.filter ?? "";
    this.users = [];
    this.page = 1;
    this.fetchUserOptions(this.page, this.pageSize, this.search);
  }
}
